import {
  toWSDate,
  WSElement,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import React from "react";
import { RouteComponentProps } from "react-router-dom";

import {
  queriesPayerEngagements,
  queriesPayerRows,
  queriesRequirements
} from "@query";
import { WSQueries } from "../../../../query/WSQuery";
import { FieldView } from "../../../../shared/components/FieldView/FieldView";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { selectorIsPayerEngagementActive } from "../../selectors/selectorIsPayerEngagementActive";
import { useRequirementInstructionsModal } from "../../../Requirements/components/RequirementsDetails/useRequirementInstructionsModal";
import { RequirementsTable } from "../../../Requirements/components/RequirementsTable";
import { selectorIsAllRequirementsComplete } from "../../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { RequirementContextType } from "../../../Requirements/types";
import { sortRequirementsByPriority } from "../../../Requirements/utils";
import { PayerIntegrationWidget } from "../../components/IntegrationsSection/PayerIntegrationWidget";
import { usePayerEngagementHistoryResources } from "../../hooks/usePayerEngagementHistoryResources";
import { getPayerEngagementHistoryUrl, PAYERS_PATH } from "../../paths";
import { AlertEligibilityRequirementPending } from "../../components/AlertEligibilityRequirementPending/EligibilityRequirementPendingAlert";
import { selectorIsInstructionsModalAvailable } from "../../../Requirements/selectors/selectorIsInstructionsModalAvailable";

type EngagementDetailsSidebarProps = RouteComponentProps<{
  engagementId: string;
  payerId: string;
}>;

export const RoutePayerEngagementDetails: React.FC<
  EngagementDetailsSidebarProps
> = ({
  match: {
    params: { engagementId, payerId }
  },
  history
}) => {
  // v3 reads: the assignment itself (name, status, timestamps and the accurate
  // per-engagement QBO mapping — the search row carries only the payer-wide
  // default), plus the search row for the client name it resolves server-side.
  const queryPayerRows = queriesPayerRows.searchPayerRows.useQuery({
    filter: { id: { eq: payerId } }
  });
  const queryEngagementQbo =
    queriesPayerEngagements.getPayerEngagement.useQuery({
      payerId,
      payerEngagementId: engagementId
    });
  // native instance read — replaces the lean requirement projection the
  // payer-row down-conversion used to carry (name/dates were unset there)
  const queryRequirements =
    queriesRequirements.listPayeeRequirements.useQueryAll({
      filter: {
        payeeEngagementId: { in: [engagementId] }
      }
    });
  const requirementInstructionsModal = useRequirementInstructionsModal();

  const historyResources = usePayerEngagementHistoryResources(engagementId);

  return (
    <WSPage>
      <WSQueries queries={{ queryPayerRows, queryEngagementQbo }}>
        {({ queryPayerRowsData, queryEngagementQboData }) => {
          const payerRow = queryPayerRowsData.data?.[0];

          // The assignment itself is the SDK resource — name, status and
          // timestamps all come off it, so the search row is needed only for the
          // client name below.
          const engagement = queryEngagementQboData;
          const engagementName = engagement.engagementName ?? undefined;

          const payerName = payerRow ? selectorSearchRowName(payerRow) : "";

          const requirements = sortRequirementsByPriority(
            queryRequirements.flatData ?? []
          );

          const areRequirementsCompleted =
            selectorIsAllRequirementsComplete(requirements);

          const isArchived = !selectorIsPayerEngagementActive(engagement);

          return (
            <WSList>
              <WSPageToolbar
                title={engagementName}
                titleAfter={
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {toWSDate(engagement.events?.updatedAt).formattedValue}
                  </WSText.ParagraphXs>
                }
                breadcrumbs={[
                  {
                    label: "Clients",
                    onClick: () => {
                      history.push(PAYERS_PATH);
                    }
                  },
                  {
                    label: payerName,
                    onClick: () => {
                      history.push(PAYERS_PATH + `/${payerId}`);
                    }
                  },
                  {
                    label: engagementName || "Assignment"
                  }
                ]}
              />
              {!areRequirementsCompleted && !isArchived ? (
                <AlertEligibilityRequirementPending />
              ) : null}
              <WSGrid>
                <WSGrid.Item span={{ m: "12", l: "8" }}>
                  <WSList gap="L">
                    <WSElement>
                      <WSSectionToolbar title="Assignment information" />
                      <WSPanel>
                        <WSList gap="XL">
                          <FieldView
                            label="Engagement name"
                            value={engagementName}
                          />
                        </WSList>
                      </WSPanel>
                    </WSElement>
                    {requirements.length ? (
                      <WSElement>
                        <WSSectionToolbar title="Assignment requirements" />
                        <WSPanel p="NONE">
                          <RequirementsTable
                            contextType={RequirementContextType.Payer}
                            requirements={requirements}
                            hideActions={isArchived}
                            onViewDetails={async (
                              requirement,
                              additionalData
                            ) => {
                              if (
                                selectorIsInstructionsModalAvailable(
                                  requirement,
                                  additionalData
                                )
                              ) {
                                const instructionsResult =
                                  await requirementInstructionsModal.open({
                                    requirementId: requirement.id!
                                  });

                                if (instructionsResult !== true) return;
                              }

                              history.push(
                                `/member/payers/${requirement.payerAccountId}/requirement/${requirement.id}`
                              );
                            }}
                          />
                        </WSPanel>
                      </WSElement>
                    ) : null}
                  </WSList>
                </WSGrid.Item>
                <WSGrid.Item span={{ m: "12", l: "4" }}>
                  <WSList gap="L">
                    <PayerIntegrationWidget
                      isInactive={isArchived}
                      payerId={payerId}
                      engagementId={engagementId}
                      customerId={engagement.quickbooksCustomerId ?? undefined}
                      itemId={engagement.quickbooksItemId ?? undefined}
                    />

                    <ResourceHistory
                      resources={historyResources}
                      onViewAll={() => {
                        history.push(
                          getPayerEngagementHistoryUrl(payerId, engagementId)
                        );
                      }}
                    />
                  </WSList>
                </WSGrid.Item>
              </WSGrid>
            </WSList>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
